import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { unselectableComponents } from '../constants';
import { buildSceneGraph } from './SceneGraph';

const DRACO_DECODER_PATH = '/draco/gltf/';

/**
 * @param {string}   modelUrl
 * @param {function} onSelect(nodeName)       - click toggles selection (add or remove)
 * @param {string}   viewMode                 - 'color' | 'wireframe' | 'translucent'
 * @param {string}   [selectedComponent]      - last-clicked component (used for currentBinding)
 * @param {string[]} [editSelectedComponents] - highlighted components (both edit and view mode)
 * @param {boolean}  [editMode]               - when true, enables multi-select behaviour
 * @param {string}   [appMode]                - 'view' | 'edit'
 * @param {object}   [viewBindings]           - bindings map; components without dashboard_id
 *                                              are dimmed and non-hoverable in view mode
 */
function Model({ modelUrl, onSelect, viewMode, selectedComponent, editSelectedComponents, editMode, appMode, viewBindings }) {
  const url = modelUrl;
  const { scene } = useGLTF(url, DRACO_DECODER_PATH);
  const [hovered, setHovered] = useState(null);

  const cachedScene = useMemo(() => scene.clone(), [scene]);

  useEffect(() => {
    return () => {
      cachedScene.traverse((child) => {
        if (child.isMesh) {
          child.geometry?.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((m) => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
    };
  }, [cachedScene]);

  // Highlighted set:
  //  - Edit mode: exclusively driven by the editSelectedComponents array (empty = nothing lit up)
  //  - View mode: group array if present, otherwise the single clicked component
  const selected = useMemo(() => {
    if (editMode) return editSelectedComponents || [];
    if (editSelectedComponents?.length) return editSelectedComponents;
    if (selectedComponent) return [selectedComponent];
    return [];
  }, [editMode, editSelectedComponents, selectedComponent]);

  const handlePointerOver = useCallback((event, nodeName) => {
    event.stopPropagation();
    // Suppress hover glow for components that have no dashboard in view mode
    if (appMode === 'view' && viewBindings && !viewBindings[nodeName]?.dashboard_id) return;
    setHovered(nodeName);
  }, [appMode, viewBindings]);

  const handlePointerOut = useCallback((event) => {
    event.stopPropagation();
    setHovered(null);
  }, []);

  // Click toggles component: select if not in selection, deselect if already selected
  const handleClick = useCallback(
    (event, nodeName) => {
      event.stopPropagation();
      if (unselectableComponents.some((u) => nodeName.includes(u))) return;
      onSelect(nodeName);
    },
    [onSelect]
  );

  const materials = useMemo(() => {
    const matDict = {};
    cachedScene.traverse((child) => {
      if (child.isMesh) {
        const nodeName = child.name;
        const mat = child.material.clone();

        if (nodeName.includes('Solid037')) {
          mat.color = new THREE.Color('dodgerblue');
        } else if (selected.includes(nodeName)) {
          mat.color = new THREE.Color('dodgerblue');
          mat.transparent = true;
          mat.opacity = 0.5;
        } else if (viewMode === 'translucent') {
          mat.transparent = true;
          mat.opacity = 0.5;
        } else {
          mat.transparent = false;
          mat.opacity = 1.0;
        }
        mat.wireframe = viewMode === 'wireframe';
        matDict[nodeName] = mat;
      }
    });
    return matDict;
  }, [cachedScene, selected, viewMode]);

  const sceneGraph = useMemo(
    () =>
      buildSceneGraph({
        object: cachedScene,
        materials,
        hovered,
        selected,
        onPointerOver: handlePointerOver,
        onPointerOut: handlePointerOut,
        onClick: handleClick,
      }),
    [cachedScene, materials, hovered, selected, handlePointerOver, handlePointerOut, handleClick]
  );

  return (
    <group dispose={null} scale={0.006}>
      {sceneGraph}
    </group>
  );
}

export default React.memo(Model);
